<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { useDefaultMessagesStore } from "@stores/survey/defaultMessages";

  const store = useDefaultMessagesStore();
  const { selectedMessage, englishMessage, editedMessages } = storeToRefs(store);
  const { updateMessage } = store;

  const saving = ref(false);

  const fields = [
    { key: "skip_text", label: "Skip text", component: "input" },
    { key: "error_message", label: "Error message", component: "input" },
    { key: "screenout_hard", label: "Screenout (hard)", component: "textarea" },
    { key: "quota_hard", label: "Quota full (hard)", component: "textarea" },
    { key: "inactive_survey", label: "Inactive survey", component: "textarea" },
    { key: "start_button", label: "Start button (m_start default label)", component: "input" },
    { key: "start_text", label: "Start text (m_start default welcome, markdown)", component: "textarea" },
    { key: "start_consent", label: "Start consent (m_start default, markdown)", component: "textarea" },
    { key: "reward_button", label: "Reward — Thanks card button", component: "input" },
    { key: "reward_decline", label: "Reward — decline link", component: "input" },
    { key: "reward_subtitle", label: "Reward — subtitle under the amount", component: "input" },
    { key: "reward_choice", label: "Reward — brand choice bullet", component: "textarea" },
    { key: "reward_delay", label: "Reward — delivery delay bullet", component: "textarea" },
    { key: "reward_email_placeholder", label: "Reward — email placeholder", component: "input" },
    { key: "reward_email_notice", label: "Reward — email privacy notice", component: "textarea" },
    { key: "reward_submit", label: "Reward — submit button", component: "input" },
    { key: "reward_edit", label: "Reward — edit link", component: "input" },
    { key: "reward_close", label: "Reward — close button", component: "input" },
  ];

  const getPlaceholder = (key) => {
    if (!englishMessage.value || selectedMessage.value?.language === "en") return "";
    return englishMessage.value.default_messages?.[key] || "";
  };

  const validationSchema = toTypedSchema(
    z.object(
      Object.fromEntries(
        fields.map((f) => [
          f.key,
          z.string({ required_error: `${f.label} cannot be blank` }).min(1, `${f.label} cannot be blank`),
        ]),
      ),
    ),
  );

  const { handleSubmit, meta, values, resetForm } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: { ...editedMessages.value },
  });

  const hasErrors = computed(() => meta.value.dirty && !meta.value.valid);

  watch(values, (v) => Object.assign(editedMessages.value, v));

  const handleSave = handleSubmit(async () => {
    saving.value = true;
    if (await updateMessage()) resetForm({ values: { ...values } });
    saving.value = false;
  });
</script>

<template>
  <Card
    v-if="selectedMessage"
    class="p-6">
    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-6">
      <CardTitle>{{ selectedMessage.name }} ({{ selectedMessage.language }})</CardTitle>
      <Button
        size="sm"
        :variant="hasErrors ? 'destructive' : 'default'"
        :disabled="saving || !meta.dirty"
        @click="handleSave">
        Save
      </Button>
    </CardHeader>
    <CardContent class="flex flex-col gap-4">
      <FormField
        v-for="field in fields"
        :key="field.key"
        v-slot="{ componentField }"
        :name="field.key">
        <FormItem>
          <FormLabel>{{ field.label }}</FormLabel>
          <FormControl>
            <Input
              v-if="field.component === 'input'"
              v-bind="componentField"
              :placeholder="getPlaceholder(field.key)" />
            <Textarea
              v-else
              v-bind="componentField"
              :placeholder="getPlaceholder(field.key)" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </CardContent>
  </Card>
</template>
